import { Dialog } from '@angular/cdk/dialog';
import { HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AttachmentList, AttachmentListItem } from '@common/data-models/complex-types/attachment-list';
import { ErrorTypes } from '@common/data-models/misc/errors';
import { ObjectProperties } from '@common/data-models/service/object-properties';
import { ServiceResponse } from '@common/data-models/service/service-response';
import { ObjectTypes } from '@common/enums/object-types';
import { Util } from '@common/utils/util';
import { saveAs } from 'file-saver';

import { BasicContentService } from './basic-content-service';
import { GlobalTablesService } from './global-tables-service';
import { LanguageSettings } from './language-settings';
import { NomWebServicesJson } from './nom-web-services-json';
import { VisualStateService } from './visual-state-service';

@Injectable({ providedIn: 'root' })
export class AttachmentService {
    // default in bytes (same as the WPF Client logic )
    public maxExternalDocumentSize: number = 512000;
    constructor(
        public nomWebServicesJson: NomWebServicesJson,
        public languageSettings: LanguageSettings,
        public basicContentService: BasicContentService,
        private visualStateService: VisualStateService,
        public globalTablesService: GlobalTablesService,
        private dialogService: Dialog
    ) {
        this.globalTablesService.globalTablesLoaded.subscribe(() => {
            if (this.visualStateService.ConfigSettings.MaxExternalDocSize) {
                this.maxExternalDocumentSize = parseInt(this.visualStateService.ConfigSettings.MaxExternalDocSize);
                if (this.maxExternalDocumentSize > 3000000) {
                    // if it's more than 3000000 bytes, limit it to 3000000
                    this.maxExternalDocumentSize = 3000000;
                }
            }
            if (window['ANDROID_WRAPPER'] && window['ANDROID_WRAPPER'].setMaxFileSize) {
                window['ANDROID_WRAPPER'].setMaxFileSize(this.maxExternalDocumentSize);
            }
        });
    }

    public async deleteAttachment(record: StorytellingRecord, attachment: AttachmentListItem): Promise<void> {
        try {
            const data = {
                ExternalFiles: record.ExternalFiles,
                Type: record.Type,
            };
            const res = await this.basicContentService.updateBasicContentV2<StorytellingRecord>(record.Database, record.Path.fullPath, record.Guid, false, data, false, record);
            if (res && res.status) {
                await this.basicContentService.deleteBasicContent(attachment.database, record.Path.fullPath, attachment.guid, ObjectTypes.EXTERNAL);
            }
        } catch (ex) {
            console.error(ex);
        }
    }

    public async createAttachment<T extends StorytellingRecord>(
        database: string,
        path: string,
        parentGuid: string,
        keepApproved: boolean,
        fields: { [id: string]: any },
        record: T
    ): Promise<ServiceResponse<T>> {
        let requestParams = {};
        let response: HttpResponse<any> = null;
        try {
            const fieldsToUpdate = this.nomWebServicesJson.convertor.toENPSFields(fields);
            Util.escapeSpecialCharsForFileName(fields);
            // always need to include create time, let WS handle expires
            fieldsToUpdate.push({
                FieldName: 'CreateTime',
                FieldValue: new Date().toJSON(),
            });
            requestParams = {
                database,
                path,
                parentGuid,
                keepApproved,
                fieldsToUpdate,
            };
            response = await this.nomWebServicesJson.put('/api/BasicContent', JSON.stringify(requestParams), {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'json',
                observe: 'response',
            });
        } catch (error) {
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Unable to create content',
                    detailMessage: `Unable to create content. \n\rStackTrace: NomWebServicesJson.createAttachment(); \n\rError: ${error}`,
                },
                ErrorTypes.WARNING
            );
            return new ServiceResponse<T>(false, `Unable to create basic content ${error.toString()}`);
        }
        // TODO fix this
        const objectProperties = new ObjectProperties(response.body);
        const newAttachments = new AttachmentList();
        const externalFiles = objectProperties.getAs<Array<any>>('ExternalFiles');
        for (const entry of externalFiles) {
            const entryObjectProperties = new ObjectProperties(entry);
            newAttachments.add(
                new AttachmentListItem(
                    entryObjectProperties.getAs<string>('DATABASE'),
                    entryObjectProperties.getAs<string>('PATH'),
                    entryObjectProperties.getAs<string>('GUID'),
                    entryObjectProperties.getAs<string>('TITLE')
                )
            );
        }
        if (record) {
            record.ExternalFiles = newAttachments;
        }
        return new ServiceResponse<T>(true, record);
    }

    public async downloadAttachment(database: string, path: string, guid: string, title: string): Promise<ServiceResponse<void>> {
        let response: Blob = null;

        if (this.visualStateService.hasIOSInterOp()) {
            window['webkit'].messageHandlers.interOp.postMessage({ name: 'startShare' });
        }

        try {
            response = await this.nomWebServicesJson.getBlob('/api/BasicContent', {
                headers: new HttpHeaders({
                    'Content-Type': 'application/json',
                    'X-ENPS-TOKEN': this.nomWebServicesJson.NomTokenId,
                }),
                responseType: 'blob',
                params: {
                    database,
                    path,
                    guid,
                    hithighlightTerm: '',
                },
            });
        } catch (error) {
            if (this.visualStateService.hasIOSInterOp()) {
                window['webkit'].messageHandlers.interOp.postMessage({ name: 'stopShare' });
            }
            this.nomWebServicesJson.showErrorModal(
                {
                    message: 'Unable to download content',
                    detailMessage: `Unable to download content. \n\rStackTrace: NomWebServicesJson.downloadAttachment(); \n\rError: ${error}`,
                },
                ErrorTypes.WARNING
            );
            return new ServiceResponse<void>(false, `Unable to download basic content ${error.toString()}`);
        }

        if (navigator.userAgent.match(/iPad|iPod|iPhone/gi) || (window['ANDROID_WRAPPER'] && window['ANDROID_WRAPPER'].saveFile)) {
            this.shareFile(response, response.type, title);
        } else {
            // placeholder for Android
            saveAs(response, title);
        }

        return new ServiceResponse<void>(true);
    }

    protected shareFile(fileblob, type, title) {
        if ((navigator.userAgent.match(/iPad|iPod|iPhone/gi) && window['webkit'] && window['webkit'].messageHandlers) || (window['ANDROID_WRAPPER'] && window['ANDROID_WRAPPER'].saveFile)) {
            const reader = new FileReader();
            reader.readAsDataURL(fileblob);
            reader.onloadend = function () {
                const data = {
                    filename: title,
                    type,
                    blob: reader.result.toString().split(',')[1],
                };
                if (window['webkit'] && window['webkit'].messageHandlers && window['webkit'].messageHandlers.interOp && window['webkit'].messageHandlers.interOp.postMessage) {
                    window['webkit'].messageHandlers.interOp.postMessage({ name: 'shareFile', fileData: JSON.stringify(data) });
                } else if (window['ANDROID_WRAPPER'] && window['ANDROID_WRAPPER'].saveFile) {
                    window['ANDROID_WRAPPER'].saveFile(JSON.stringify(data));
                }
            };
        }
    }
}
